"use client";

import React, {useMemo} from "react";
import { Stack, Typography } from "@mui/material";
import {useLocale, useTranslations} from "next-intl";
import { ColletteDivider, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Close } from "@mui/icons-material";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import { useForm } from "react-hook-form";
import DataLayer from "@/components/data-layer/data-layer";
import useLeadSubmit from "@/hooks/forms/on-submit/use-lead-submit";
import {TTAEFormData} from "@/types/leads/lead-forms";
import TalkToAnExpertSubmissionMessage from "@/components/forms/talk-to-an-expert/talk-to-an-expert-submission-message";
import TalkToAnExpertForm from "@/components/forms/talk-to-an-expert/talk-to-an-expert-form";
import ttaeDataLayerPushFactory from "@/factories/data-layer/ttae-data-layer-push-factory";

interface TalkToAnExpertProps {
  handleClose: () => void;
}

const TalkToAnExpertSection = ({
  handleClose
}: TalkToAnExpertProps) => {
  const t = useTranslations("FORMS.TTAE");
  const sourceUrl = window.location.href;
  const locale = useLocale();

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, isSubmitted, isSubmitting, isValid },
    control,
    getValues
  } = useForm<TTAEFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isTravelAgent: false,
      workingWithAgent: false,
      withMoreTravelers: false,
      recaptchaToken: undefined
    }
  });

  const { onSubmit, serverError } = useLeadSubmit<TTAEFormData>({
    locale,
    sourceUrl,
    leadType: "TTAE"
  });
  
  const dataLayerData = useMemo(() => 
    ttaeDataLayerPushFactory(getValues(), sourceUrl),
    [getValues, sourceUrl, isSubmitSuccessful]); // needs to update when isSubmitted changes

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default"
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        gap="1rem"
        alignItems="flex-start"
        padding={{ xs: "2rem 2.4rem", md: "4rem 4rem 0" }}
        sx={{ justifyContent: isSubmitSuccessful ? "end" : ""}}
      >
        {!isSubmitSuccessful && ( 
          <Stack flexGrow={1}>
          <Typography variant="h3" mb="1.2rem" data-testid="ttae-header">
            {t("Talk to an Expert")}
          </Typography>
          {!isSubmitSuccessful && (
            <Typography mb="2.4rem" data-testid="ttae-copy">
              {t("copy")}
            </Typography>
          )}
          <ColletteDivider sx={{ borderColor: "divider" }} />
        </Stack>
        )}
        <ColletteIconButton
          testId="dialog-close-button"
          onClick={handleClose}
          sx={{
            mt: "-0.8rem",
            mr: "-0.8rem"
          }}
        >
          <Close />
        </ColletteIconButton>
      </Stack>
      {/* Form and submission response */}
      {isSubmitSuccessful && (
        <TalkToAnExpertSubmissionMessage handleClose={handleClose} error={serverError} />
      )}
      {!isSubmitSuccessful && (
        <TalkToAnExpertForm
          isInvalid={!isValid && isSubmitted}
          control={control}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
        />
      )}
      {/* Privacy Policy */}
      {!isSubmitSuccessful && <HubspotFormDisclaimer testId="ttae-disclaimer" />}
      {isSubmitSuccessful && <DataLayer data={dataLayerData} />}
    </Stack>
  );
};

export default TalkToAnExpertSection;
